import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useStore } from '../context/StoreContext';
import { addMonths, addYears, format } from 'date-fns';

const { FiX, FiRefreshCw, FiCheck, FiTag } = FiIcons;

const RecurringExpenseModal = ({ isOpen, onClose, onSave, initialData, properties }) => {
  const { data } = useStore();
  const [formData, setFormData] = useState({
    propertyId: '',
    amount: '',
    category: '',
    description: '',
    frequency: 'Monthly',
    nextDueDate: format(new Date(), 'yyyy-MM-dd'),
    tags: ['Automatic'],
    status: 'Active'
  });

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({
        propertyId: '',
        amount: '',
        category: data.expenseCategories[0] || '',
        description: '',
        frequency: 'Monthly',
        nextDueDate: format(new Date(), 'yyyy-MM-dd'),
        tags: ['Automatic'],
        status: 'Active'
      });
    }
  }, [initialData, isOpen, data]);

  const toggleTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag]
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-200">
        <div className="flex justify-between items-center p-6 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl">
              <SafeIcon icon={FiRefreshCw} />
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-900">Automation Rule</h2>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Recurring Expenditure</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <SafeIcon icon={FiX} size={24} />
          </button>
        </div>

        <form className="p-8 space-y-6 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Target Property</label>
              <select 
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none"
                value={formData.propertyId}
                onChange={e => setFormData({ ...formData, propertyId: e.target.value })}
              >
                <option value="">Select Target...</option>
                <option value="shared">General Portfolio</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Amount ($)</label>
              <input 
                type="number" 
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none"
                value={formData.amount}
                onChange={e => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Frequency</label>
              <select 
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none"
                value={formData.frequency}
                onChange={e => setFormData({ ...formData, frequency: e.target.value })}
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Next Billing Date</label>
              <input 
                type="date"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-sm outline-none"
                value={formData.nextDueDate}
                onChange={e => setFormData({ ...formData, nextDueDate: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Tags</label>
            <div className="flex flex-wrap gap-2">
              {data.transactionTags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase border transition-all ${
                    formData.tags.includes(tag) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-400 border-gray-100'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Description</label>
            <textarea 
              rows="2"
              className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl text-sm font-medium outline-none"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g. Fiber Internet Service"
            />
          </div>
        </form>

        <div className="p-6 border-t border-gray-100 bg-white flex justify-end gap-3">
          <button onClick={onClose} className="px-6 py-3 font-bold text-gray-400 hover:text-gray-600">Cancel</button>
          <button 
            onClick={() => { onSave(formData); onClose(); }}
            className="px-10 py-3 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 flex items-center gap-2"
          >
            <SafeIcon icon={FiCheck} /> Save Schedule
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecurringExpenseModal;